/**
 * LeagueActivityPage — full-history view of a league's roster moves.
 *
 * 2026-09-03 (owner request) — "See all" from the Stage Transfer Activity
 * card. Pages through GET /leagues/{id}/transfer-activity with the
 * `before` cursor, grouped by day. Mobile mirror: LeagueActivityScreen.js
 */
import { useCallback, useEffect, useState } from 'react';
import { ArrowLeftRight } from 'lucide-react';
import BackButton from '../components/BackButton';
import { Button } from '../components/ui/button';
import { Skeleton } from '../components/ui/skeleton';
import { MoveRow } from '../components/TransferActivity';
import { leagueAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';

const PAGE = 50;

export function dayLabel(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const now = new Date();
  const startOf = (x) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const diffDays = Math.round((startOf(now) - startOf(d)) / 86_400_000);
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  const opts = { month: 'short', day: 'numeric' };
  if (d.getFullYear() !== now.getFullYear()) opts.year = 'numeric';
  return d.toLocaleDateString(undefined, opts);
}

export default function LeagueActivityPage({ selectedLeagueId, leagueName, onTeamClick, onEntityClick, onBack }) {
  const { user } = useAuth();
  const [moves, setMoves] = useState([]);
  const [nextBefore, setNextBefore] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);

  const fetchPage = useCallback(async (before) => {
    const res = await leagueAPI.transferActivity(selectedLeagueId, { limit: PAGE, before: before || undefined });
    return res.data || {};
  }, [selectedLeagueId]);

  useEffect(() => {
    if (!selectedLeagueId) return;
    let alive = true;
    setLoading(true);
    fetchPage(null)
      .then((d) => {
        if (!alive) return;
        setMoves(d.moves || []);
        setHasMore(!!d.has_more);
        setNextBefore(d.next_before || null);
      })
      .catch(() => { if (alive) setMoves([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [selectedLeagueId, fetchPage]);

  const loadMore = async () => {
    if (!hasMore || loadingMore) return;
    setLoadingMore(true);
    try {
      const d = await fetchPage(nextBefore);
      setMoves((prev) => [...prev, ...(d.moves || [])]);
      setHasMore(!!d.has_more);
      setNextBefore(d.next_before || null);
    } catch { /* keep what we have */ }
    finally { setLoadingMore(false); }
  };

  let lastDay = null;

  return (
    <div className="p-4" data-testid="league-activity-page">
      {onBack && <BackButton onClick={onBack} />}
      <h2 className="text-lg font-semibold tracking-tight mb-1" style={{ fontFamily: 'var(--font-heading)' }}>
        <ArrowLeftRight className="h-5 w-5 inline mr-2 text-[hsl(var(--accent))]" />
        League Activity
      </h2>
      <p className="text-xs text-muted-foreground mb-4" data-testid="league-activity-subtitle">
        {leagueName ? `${leagueName} · ` : ''}
        {loading ? 'loading…' : `${moves.length}${hasMore ? '+' : ''} ${moves.length === 1 ? 'move' : 'moves'}`}
      </p>

      {loading ? (
        <div className="space-y-2">{[1, 2, 3, 4, 5].map((i) => <Skeleton key={i} className="h-12 w-full rounded-lg" />)}</div>
      ) : moves.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-10" data-testid="league-activity-empty">
          No roster moves yet — be the first to shake up the league.
        </p>
      ) : (
        <div className="bg-card border border-border/60 rounded-2xl p-3 space-y-1">
          {moves.map((m) => {
            const day = dayLabel(m.created_at);
            const showDay = day !== lastDay;
            lastDay = day;
            return (
              <div key={m.id}>
                {showDay && (
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold px-2 pt-3 pb-1 first:pt-0" data-testid="league-activity-day">
                    {day}
                  </p>
                )}
                <MoveRow m={m} isMe={m.user_id === user?.id} user={user} onTeamClick={onTeamClick} onEntityClick={onEntityClick} />
              </div>
            );
          })}
          {hasMore && (
            <div className="pt-3 flex justify-center">
              <Button variant="outline" size="sm" onClick={loadMore} disabled={loadingMore} data-testid="league-activity-load-more">
                {loadingMore ? 'Loading…' : 'Load older moves'}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
